import { useEffect, useRef, useState } from 'react'

import { getPairScrollTarget, type ScrollDirection } from './pairScroll'

const EDGE_TOLERANCE = 4

export function usePairScroll(itemsCount: number) {
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const updateScrollState = () => {
      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
      setCanScrollLeft(viewport.scrollLeft > EDGE_TOLERANCE)
      setCanScrollRight(maxScrollLeft - viewport.scrollLeft > EDGE_TOLERANCE)
    }

    updateScrollState()

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(viewport)
    Array.from(viewport.children).forEach((child) =>
      resizeObserver.observe(child)
    )
    viewport.addEventListener('scroll', updateScrollState, { passive: true })

    const scrollByWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return

      const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth
      const atStart = viewport.scrollLeft <= 0 && event.deltaY < 0
      const atEnd = viewport.scrollLeft >= maxScrollLeft - 1 && event.deltaY > 0
      if (maxScrollLeft <= 0 || atStart || atEnd) return

      event.preventDefault()
      viewport.scrollLeft += event.deltaY
    }
    viewport.addEventListener('wheel', scrollByWheel, { passive: false })

    return () => {
      resizeObserver.disconnect()
      viewport.removeEventListener('scroll', updateScrollState)
      viewport.removeEventListener('wheel', scrollByWheel)
    }
  }, [itemsCount])

  const scrollPair = (direction: ScrollDirection) => {
    const viewport = viewportRef.current
    if (!viewport) return

    const items = Array.from(viewport.children).flatMap((child) =>
      child instanceof HTMLElement
        ? [{ left: child.offsetLeft, width: child.offsetWidth }]
        : []
    )
    viewport.scrollTo({
      left: getPairScrollTarget(items, viewport.scrollLeft, direction),
      behavior: 'smooth',
    })
  }

  return { viewportRef, canScrollLeft, canScrollRight, scrollPair }
}
